import React from 'react';
import { Link, Project } from '../../StateContext';
import { secondsToHours, totalProjectBaseline, hoursForType } from '../Links/weekView';
import { useLanguage } from '../../Services/LanguageContext';

interface IProps {
    estimatedTimeInSeconds: number;
    link: Link[];
    project?: Project;
}

const TotalHourOverview = (props: IProps) => {
    const { t } = useLanguage();
    const projectTypes = props.project?.types ?? [];
    const estimatedHours = projectTypes.length > 0
        ? projectTypes.reduce((sum, pt) => sum + secondsToHours(pt.calculatedTimeInSeconds), 0)
        : secondsToHours(props.estimatedTimeInSeconds);
    const dhzSeconds     = props.project?.dhzSeconds ?? 0;
    const meerwerkHours  = secondsToHours(props.project?.meerwerkSeconds ?? 0);
    const dhzHours       = secondsToHours(dhzSeconds);

    const baseline  = totalProjectBaseline(props.link, dhzSeconds);
    const remaining = estimatedHours - baseline;
    const isPositive = remaining >= 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
            {/* Primary remaining pill */}
            <div
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: 700,
                    background: isPositive ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                    color: isPositive ? 'var(--success)' : 'var(--danger)',
                    border: `1px solid ${isPositive ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                    transition: 'all 0.3s ease',
                }}
            >
                <span style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: isPositive ? 'var(--success)' : 'var(--danger)',
                    boxShadow: isPositive ? '0 0 8px rgba(34,197,94,0.6)' : '0 0 8px rgba(239,68,68,0.6)',
                }} />
                {remaining.toFixed(2)}{t('hourSuffix')} {isPositive ? t('remaining') : t('over')}
            </div>

            {/* Per-type breakdown — only when types are defined */}
            {projectTypes.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {projectTypes.map((pt) => {
                        const typeEstimated = secondsToHours(pt.calculatedTimeInSeconds);
                        const typeActual = hoursForType(props.link, pt.id);
                        const typeRemaining = typeEstimated - typeActual;
                        const pos = typeRemaining >= 0;
                        return (
                            <span key={pt.id} style={typePill(pos)}>
                                {pt.name}: {typeRemaining.toFixed(2)}{t('hourSuffix')}
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Extra badges — only when non-zero */}
            {(meerwerkHours > 0 || dhzHours > 0) && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {meerwerkHours > 0 && (
                        <span style={pill('#1976d2')}>
                            MW +{meerwerkHours.toFixed(2)}{t('hourSuffix')}
                        </span>
                    )}
                    {dhzHours > 0 && (
                        <span style={pill('#c62828')}>
                            DHZ +{dhzHours.toFixed(2)}{t('hourSuffix')}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

const typePill = (positive: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 700,
    color: positive ? 'var(--success)' : 'var(--danger)',
    background: positive ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
    border: `1px solid ${positive ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
});

const pill = (color: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 700,
    color,
    background: `${color}18`,
    border: `1px solid ${color}40`,
});

export default TotalHourOverview;
